import {Inject, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {exhaustMap, tap, withLatestFrom} from 'rxjs/operators';
import {EMPTY, Observable} from 'rxjs';
import {HttpErrorResponse} from "@angular/common/http";

import {
  InsightEntityType,
  RecommendationGroupModel, RecommendationHolderType,
  RecommendationModel,
  RecommendationStatus,
} from "../../models/recommendation.model";
import {RecommendationHttpService} from "../services/recommendation-http.service";
import * as _ from 'lodash';
import {CommonsFacadeService} from "../../../commons/angular/state/commons/commons-facade.service";

export interface RecommendationGroupsComponentState {
  recommendationGroups: RecommendationGroupModel[];
  selectedRecommendationGroup?: RecommendationGroupModel;
  recommendations?: RecommendationModel[];
  cursor?: string;
  loading: boolean;
  error: string | null;
}

@Injectable()
export class RecommendationGroupsComponentStore extends ComponentStore<RecommendationGroupsComponentState> {

  // Selectors
  readonly recommendationGroups$ = this.select((state) => state.recommendationGroups);
  readonly selectedRecommendationGroup$ = this.select((state) => state.selectedRecommendationGroup);
  readonly recommendations$ = this.select((state) => state.recommendations);
  readonly loading$ = this.select((state) => state.loading);
  readonly error$ = this.select((state) => state.error);
  readonly cursor$ = this.select((state) => state.cursor);

  vm$ = this.select({
    recommendationGroups: this.recommendationGroups$,
    selectedRecommendationGroup: this.selectedRecommendationGroup$,
    recommendations: this.recommendations$,
    loading: this.loading$,
    error: this.error$,
    cursor: this.cursor$
  })

  constructor(
    @Inject(RecommendationHttpService) private recommendationHttpService: RecommendationHttpService,
    @Inject(CommonsFacadeService) private commonsFacadeService: CommonsFacadeService,
  ) {
    super({
      recommendationGroups: [],
      selectedRecommendationGroup: null,
      recommendations: [],
      cursor: '',
      loading: false,
      error: null,
    });
  }

  // Updaters
  readonly onSetRecommendationGroups = this.updater((state: RecommendationGroupsComponentState, value: {
    recommendationGroups: RecommendationGroupModel[],
    cursor?: string
  }) => ({
    ...state,
    recommendationGroups: _.uniq([...state.recommendationGroups, ...value.recommendationGroups]),
    cursor: value.cursor,
    loading: false,
    error: null,
  }));

  readonly onResetRecommendationGroups = this.updater((state: RecommendationGroupsComponentState) => ({
    ...state,
    recommendationGroups: [],
    selectedRecommendationGroup: null,
    recommendations: [],
    cursor: null,
    loading: false,
    error: null,
  }));

  readonly setSelectedGroupRecommendation = this.updater((
    state: RecommendationGroupsComponentState,
    value: {
      selectedRecommendationGroup: RecommendationGroupModel,
      recommendations: RecommendationModel[]
    }) => ({
    ...state,
    selectedRecommendationGroup: value.selectedRecommendationGroup,
    recommendations: value.recommendations
  }));

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  readonly setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    error: error.message,
    loading: false,
  }));

  // Effect for fetching recommendations
  readonly getRecommendationGroups = this.effect(
    (trigger$: Observable<{
      siteId: string,
      recommendationHolderId: string
      recommendationHolderType: RecommendationHolderType,
      entityType: InsightEntityType
    }>) =>
      trigger$.pipe(
        tap(() => this.setLoading(true)), // Set loading state
        withLatestFrom(this.cursor$, this.recommendationGroups$), // Combine with cursor from the state
        exhaustMap(([params, cursor, recommendationGroups]) => {
            // don't fetch the next group if there's no cursor value and list already populated
            if (!cursor && recommendationGroups.length > 0) {
              this.setLoading(false);
              return EMPTY;
            }

            return this.recommendationHttpService.getRecommendationGroups(
              params.siteId, params.recommendationHolderId, params.recommendationHolderType,
              params.entityType, 100, cursor || '').pipe(
              tap({
                next: (response) => {
                  return this.onSetRecommendationGroups({recommendationGroups: response.list, cursor: response.cursor});
                },
                error: (err) => this.setError(err.message || 'Failed to fetch recommendations'),
                finalize: () => this.setLoading(false)
              })
            )
          }
        )
      )
  );


  // Observable<RecommendationGroupModel>
  //{
  //       siteId: string,
  //       recommendationHolderId: string
  //       recommendationHolderType: RecommendationHolderType,
  //       entityType: InsightEntityType
  //     }
  // siteId: string, recommendationHolderId: string,
  //                           holderType: RecommendationHolderType,
  //                           entityId: string, entityType: InsightEntityType,
  //                           limit: number
  readonly selectGroupRecommendation = this.effect(
    (trigger$: Observable<{
      siteId: string,
      holderId: string
      holderType: RecommendationHolderType,
      group: RecommendationGroupModel
    }>) => {
      return trigger$.pipe(
        tap(() => this.setLoading(true)),
        exhaustMap(params => {
          return this.recommendationHttpService
            .getGroupRecommendations(params.siteId, params.holderId, params.holderType, params.group.id, params.group.type, 100)
            .pipe(
              tap((results) => this.setSelectedGroupRecommendation({
                selectedRecommendationGroup: params.group,
                recommendations: results
              })),
              tap({
                finalize: () => this.setLoading(false)
              })
            );
        }),
      );
    },
  );

  readonly resetGroupRecommendation = this.updater((state) => ({
    ...state,
    selectedRecommendationGroup: null,
    recommendations: []
  }));

  readonly onUpdateRecommendation = this.updater((state, recommendation: RecommendationModel) => ({
    ...state,
    recommendations: state.recommendations.map((rec) => (rec.id === recommendation.id ? recommendation : rec))
  }));

  readonly onMarkRecommendationAsRead = this.updater((state, markedRecommendation: RecommendationModel) => {
    const updatedRecommendations = state.recommendations.filter((recommendation: RecommendationModel) => markedRecommendation.id !== recommendation.id);
    const isGroupEmpty = updatedRecommendations.length === 0;

    const updatedSelectedGroup = isGroupEmpty
      ? null
      : state.selectedRecommendationGroup
        ? {...state.selectedRecommendationGroup, total: updatedRecommendations.length}
        : null;

    let updatedGroups = state.recommendationGroups;
    if (state.selectedRecommendationGroup) {
      updatedGroups = updatedGroups.map(group =>
        group.id === state.selectedRecommendationGroup.id
          ? {...group, total: updatedRecommendations.length}
          : group
      );
    }

    if (isGroupEmpty && state.selectedRecommendationGroup) {
      updatedGroups = updatedGroups.filter(
        group => group.id !== state.selectedRecommendationGroup.id
      );
    }

    return {
      ...state,
      recommendationGroups: updatedGroups,
      recommendations: updatedRecommendations,
      selectedRecommendationGroup: {...updatedSelectedGroup}
    };
  });

  readonly updateRecommendation = this.effect((recommendation$: Observable<RecommendationModel>) =>
    recommendation$.pipe(
      tap(() => this.commonsFacadeService.setLoaderOn()),
      exhaustMap((recommendation) =>
        this.recommendationHttpService.updateRecommendation(recommendation).pipe(
          tap({
            next: (updatedRecommendation) => {
              this.onUpdateRecommendation(updatedRecommendation);
            },
            error: (err) => this.setError(err.message || 'Failed to update recommendation'),
            finalize: () => this.commonsFacadeService.setLoaderOff()
          })
        )
      )
    )
  );

  readonly markRecommendationAsRead = this.effect((recommendation$: Observable<RecommendationModel>) =>
    recommendation$.pipe(
      tap(() => this.commonsFacadeService.setLoaderOn()),
      exhaustMap((recommendation) =>
        this.recommendationHttpService.markRecommendationAsRead(recommendation).pipe(
          tap({
            next: (markedRecommendation) => {
              this.onMarkRecommendationAsRead(markedRecommendation);
            },
            error: (err) => this.setError(err.message || 'Failed to mark recommendation as read'),
            finalize: () => this.commonsFacadeService.setLoaderOff()
          })
        )
      )
    )
  );
}
